import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  TextField,
  Button,
  MenuItem,
  Grid,
  Container,
  Typography,
  Card,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import toast from "react-hot-toast";

export default function AdminFaqs() {
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [type, setType] = useState("speaker");

  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);

  const token = localStorage.getItem("token");

  // EDIT MODAL STATE
  const [editOpen, setEditOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editQuestion, setEditQuestion] = useState("");
  const [editAnswer, setEditAnswer] = useState("");
  const [editType, setEditType] = useState("");

  // ADD MODAL STATE
  const [addOpen, setAddOpen] = useState(false);

  const openAddModal = () => setAddOpen(true);
  const closeAddModal = () => setAddOpen(false);

  const categories = [
    { label: "Abstract", value: "abstract" },
    { label: "Speakers", value: "speaker" },
    { label: "Registration", value: "registration" },
  ];

  // =============================================
  // LOAD FAQs
  // =============================================
  const loadFaqs = async () => {
    setLoading(true);
    try {
      const url = `https://eventscons.com/eventbackendnew/api/faqs?page=${
        page + 1
      }&per_page=10&type=${type}`;

      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setFaqs(res.data.faqs || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load FAQs");
    }
    setLoading(false);
  };

  useEffect(() => {
    loadFaqs();
  }, [page, type]);

  // =============================================
  // CREATE FAQ (NEW MODAL)
  // =============================================
  const handleAddFaq = async () => {
    if (!question || !answer || !type) {
      toast.error("All fields are required!");
      return;
    }

    try {
      const payload = { question, answer, type, status: 1 };

      await axios.post("https://eventscons.com/eventbackendnew/api/faqs", payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      toast.success("FAQ Created!");

      setQuestion("");
      setAnswer("");

      closeAddModal();
      loadFaqs();
    } catch (err) {
      console.error(err);
      toast.error("Failed to create FAQ");
    }
  };

  // =============================================
  // DELETE FAQ
  // =============================================
  const deleteFAQ = async (id) => {
    try {
      await axios.delete(`https://eventscons.com/eventbackendnew/api/faqs/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success("FAQ Deleted!");
      loadFaqs();
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete FAQ");
    }
  };

  // =============================================
  // OPEN EDIT MODAL
  // =============================================
  const openEditModal = (row) => {
    setEditId(row.id);
    setEditQuestion(row.question);
    setEditAnswer(row.answer);
    setEditType(row.type);
    setEditOpen(true);
  };

  // =============================================
  // UPDATE FAQ
  // =============================================
  const updateFAQ = async () => {
    try {
      const payload = {
        question: editQuestion,
        answer: editAnswer,
        type: editType,
        status: 1,
      };

      await axios.put(
        `https://eventscons.com/eventbackendnew/api/faqs/${editId}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      toast.success("FAQ Updated Successfully!");
      setEditOpen(false);
      loadFaqs();
    } catch (err) {
      console.error(err);
      toast.error("Update failed!");
    }
  };

  // =============================================
  // TABLE COLUMNS
  // =============================================
  const columns = [
    { field: "question", headerName: "Question", flex: 1 },
    { field: "answer", headerName: "Answer", flex: 1 },
    { field: "type", headerName: "Category", width: 150 },
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      renderCell: (params) => (
        <>
          <IconButton
            color="primary"
            onClick={() => openEditModal(params.row)}
          >
            <EditIcon />
          </IconButton>

          <IconButton color="error" onClick={() => deleteFAQ(params.row.id)}>
            <DeleteIcon />
          </IconButton>
        </>
      ),
    },
  ];

  return (
    <Container maxWidth="lg" sx={{ mt: 5 }}>
      {/* TITLE */}
      <Typography
        variant="h3"
        align="center"
        sx={{ fontWeight: 700, color: "#C62828", mb: 5 }}
      >
        All FAQs
      </Typography>

      {/* CATEGORY + ADD BUTTON */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <TextField
            select
            label="Select Category"
            fullWidth
            value={type}
            onChange={(e) => setType(e.target.value)}
          >
            {categories.map((cat) => (
              <MenuItem key={cat.value} value={cat.value}>
                {cat.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* ADD FAQ BUTTON */}
        <Grid
          item
          xs={12}
          md={4}
          sx={{ display: "flex", justifyContent: "flex-end" }}
        >
          <Button
            variant="contained"
            onClick={openAddModal}
            sx={{
              backgroundColor: "#9B1322",
              "&:hover": { backgroundColor: "#7f0f1c" },
              color: "#fff",
              textTransform: "none",
              height: "100%",
            }}
          >
            Add FAQ
          </Button>
        </Grid>
      </Grid>

      {/* TABLE */}
      <Card sx={{ p: 2, mb: 5 }}>
        <div style={{ height: 500, width: "100%" }}>
          <DataGrid
            rows={faqs}
            columns={columns}
            page={page}
            onPageChange={(newPage) => setPage(newPage)}
            pageSize={10}
            rowCount={total}
            paginationMode="server"
            loading={loading}
            getRowId={(row) => row.id}
          />
        </div>
      </Card>

      {/* ADD FAQ MODAL */}
      <Dialog open={addOpen} onClose={closeAddModal} fullWidth maxWidth="sm">
        <DialogTitle>Add New FAQ</DialogTitle>

        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Question"
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Answer"
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Category"
                value={type}
                onChange={(e) => setType(e.target.value)}
              >
                {categories.map((cat) => (
                  <MenuItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={closeAddModal}>Cancel</Button>
          <Button
            onClick={handleAddFaq}
            variant="contained"
            sx={{
              backgroundColor: "#9B1322",
              "&:hover": { backgroundColor: "#7f0f1c" },
              color: "#fff",
            }}
          >
            Add FAQ
          </Button>
        </DialogActions>
      </Dialog>

      {/* EDIT FAQ MODAL */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Edit FAQ</DialogTitle>

        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Question"
                value={editQuestion}
                onChange={(e) => setEditQuestion(e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Answer"
                value={editAnswer}
                onChange={(e) => setEditAnswer(e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Category"
                value={editType}
                onChange={(e) => setEditType(e.target.value)}
              >
                {categories.map((cat) => (
                  <MenuItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button
            onClick={updateFAQ}
            variant="contained"
            sx={{
              backgroundColor: "#9B1322",
              "&:hover": { backgroundColor: "#7f0f1c" },
              color: "#fff",
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
